import { type ReactNode, useMemo } from 'react';

import type { VNFieldHelpProps } from './types';
import { hasFieldHelpIcon } from './types';
import { useVNFieldHelpController } from './useVNFieldHelpController';
import VNFieldHelpChrome from './VNFieldHelpChrome';
import type { VNFieldHelpIconConfig } from './VNFieldHelpContext';
import { VNFieldHelpProvider } from './VNFieldHelpContext';
import VNFieldHelpIconButton from './VNFieldHelpIconButton';
import VNFieldHelpLabel from './VNFieldHelpLabel';

type Props = VNFieldHelpProps & {
  label?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
  iconPlacement?: 'in-field' | 'beside-label';
  /**
   * overlay: absolute slot on the control (TextField).
   * adornment: child places icon in InputProps.endAdornment (Select, Autocomplete).
   * trigger: child renders icon in dropdown trigger (VNDropdown).
   */
  iconAttach?: 'overlay' | 'adornment' | 'trigger';
};

const VNFieldHelpLayout = ({
  label = '',
  required = false,
  children,
  className,
  iconPlacement = 'in-field',
  iconAttach = 'overlay',
  ...fieldHelpProps
}: Props) => {
  const showIcon = hasFieldHelpIcon(fieldHelpProps);
  const {
    containerRef,
    panelOpen,
    drawerOpen,
    isActive,
    clickable,
    handleIconClick,
    handleOpenDrawerFromPanel,
    drawerAriaLabel,
    helperTooltip,
    helperText,
    helperContent,
    helperLink,
    helperContentTitle,
    closeDrawer,
  } = useVNFieldHelpController(fieldHelpProps);

  const iconConfig: VNFieldHelpIconConfig | null = useMemo(() => {
    if (!showIcon || iconPlacement !== 'in-field') return null;
    return {
      active: isActive,
      helperTooltip,
      onClick: clickable ? handleIconClick : undefined,
      ariaLabel: drawerAriaLabel,
    };
  }, [showIcon, iconPlacement, isActive, helperTooltip, clickable, handleIconClick, drawerAriaLabel]);

  const providerIconConfig = useMemo(() => (iconAttach !== 'overlay' ? iconConfig : null), [iconAttach, iconConfig]);

  const rootClass = [
    'vn-field-help',
    iconPlacement === 'in-field' && 'vn-field-help--in-field',
    iconAttach === 'adornment' && 'vn-field-help--adornment',
    panelOpen && 'vn-field-help--help-open',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <VNFieldHelpProvider iconConfig={providerIconConfig}>
      <div className={rootClass} ref={containerRef}>
        {iconPlacement === 'beside-label' && label != null && label !== '' && (
          <VNFieldHelpLabel
            label={label}
            required={required}
            helperTooltip={helperTooltip}
            showIcon={showIcon}
            clickable={clickable}
            panelOpen={isActive}
            onIconClick={clickable ? handleIconClick : undefined}
            drawerAriaLabel={drawerAriaLabel}
          />
        )}
        <div className="vn-field-help__control">
          {children}
          {iconConfig && iconPlacement === 'in-field' && iconAttach === 'overlay' && (
            <div className="vn-field-help__icon-slot">
              <VNFieldHelpIconButton {...iconConfig} />
            </div>
          )}
        </div>
        <VNFieldHelpChrome
          panelOpen={panelOpen}
          drawerOpen={drawerOpen}
          helperText={helperText}
          helperLink={helperLink}
          helperContent={helperContent}
          helperContentTitle={helperContentTitle}
          onOpenDrawerFromPanel={handleOpenDrawerFromPanel}
          onCloseDrawer={closeDrawer}
        />
      </div>
    </VNFieldHelpProvider>
  );
};

export default VNFieldHelpLayout;
